/**
 * **主题运行时读回** —— 通道 ④：真正「不只是 CSS」的那一半。
 *
 * ## 为什么必须有它
 *
 * 有几处视觉**根本不经过 CSS**，`var(--np-*)` 够不到：
 *
 * ```text
 *   连线描边        Vue Flow 的 style 对象    Playground.vue / useGestureMachine.ts
 *   小地图配色      MiniMap 的 props          maskColor / nodeColor / nodeStroke
 *   框选矩形        SVG 属性
 * ```
 *
 * 现在的写法是把颜色**再从 JS 里硬编码一遍**（`MUTED_EDGE_STROKE = '#e05252'`、
 * `light.value ? A : B` 的三元），于是主题换了颜色，这些东西不跟。
 *
 * ## 解法：单一真相留在 DOM，JS 反向读
 *
 * ```text
 *   主题 →  CSS 变量  →  ┬─→ CSS 级联      （绝大多数视觉）
 *                        └─→ getComputedStyle 读回 → JS 绘制
 * ```
 *
 * 好处是**主题作者不需要知道 JS 的存在**：他只要设变量 —— 哪怕是通过
 * 通道 ③ 的任意 CSS 设的 —— JS 侧自动跟上。
 *
 * ## ⚠️ 两条纪律（都是这个仓库踩过的）
 *
 * 1. **读回必须合批**，不能在每个 computed 里各自 `getComputedStyle`。
 *    `getComputedStyle` 会强制同步重排，散着调 = 拖动时每帧多次重排。
 *    这里的做法是：登记需要的变量名 → `requestAnimationFrame` 里**一次性**
 *    从 `documentElement` 读全部 → 存进 `shallowRef`。computed 只读那个
 *    普通对象，不碰 DOM。
 *
 * 2. **读出空串要有兜底**。变量没定义时 `getPropertyValue` 返回 `''`，
 *    直接拿去当 `stroke` 会得到 `stroke=""` → 线条**不可见**，
 *    而这种失败看起来像「图层被藏了」。所以每个取值点都必须给兜底。
 */
import { computed, shallowRef, type ComputedRef } from 'vue'
import type { SocketType } from '../types/socket'
import { SOCKET_COLORS } from './tokens'

/** 已被登记、需要读回的变量名 */
const requested = new Set<string>()

/** 读回结果。**只读普通对象** —— computed 读它不会触发任何布局 */
const snapshot = shallowRef<Record<string, string>>({})

let scheduled = false

const hasDom = (): boolean =>
  typeof document !== 'undefined' && typeof getComputedStyle === 'function'

/**
 * 登记一个变量，并安排一次读回。
 *
 * 重复调用同一个名字是廉价的（Set 去重，且已调度就不再调度）。
 */
export function ensureThemeVar(name: string): void {
  if (!name.startsWith('--')) return
  requested.add(name)
  /*
   * ★ **已经在快照里就不再调度。**
   *
   * ⚠️ 这一行是防**自激循环**的，不能删。
   *
   * 调用链是这样的：渲染 → `themeCategoryColor()` → `ensureThemeVar` →
   * 调度 rAF → 读回 → 换掉 `snapshot` → **触发重渲染** →
   * 又调 `ensureThemeVar` → 又调度……于是页面以 60fps 空转，
   * 测试里表现为「挂载之后永远不结束」。
   *
   * 这个 bug 真出现过：`verify:render` 从几十秒变成**永不返回**。
   * 静态检查全绿，因为它是纯粹的时间行为。
   *
   * 判据用「键在不在快照里」而不是「值一不一样」：键在就说明
   * **这一轮已经读过它**，值是空的（变量确实没定义）也是有效结论。
   */
  if (name in snapshot.value) return
  scheduleReadback()
}

/** 登记一批 */
export function ensureThemeVars(names: readonly string[]): void {
  let need = false
  for (const n of names) {
    requested.add(n)
    if (!(n in snapshot.value)) need = true
  }
  if (need) scheduleReadback()
}

function scheduleReadback(): void {
  if (scheduled || !hasDom()) return
  scheduled = true
  /*
   * ⚠️ 用 rAF 而不是微任务：主题应用往往紧跟一堆 DOM 变更，
   * 立刻读会读到**改到一半**的样式（然后被缓存住，看起来像「没生效」）。
   * rAF 保证在样式算完之后。
   */
  const raf =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb: () => void) => setTimeout(cb, 16)
  raf(() => {
    scheduled = false
    readback()
  })
}

/** 立刻读一遍（内部用；导出是为了让验证脚本能同步驱动） */
export function readback(): void {
  if (!hasDom()) return
  const cs = getComputedStyle(document.documentElement)
  const prev = snapshot.value
  const next: Record<string, string> = {}
  let changed = Object.keys(prev).length !== requested.size
  for (const name of requested) {
    const value = cs.getPropertyValue(name).trim()
    next[name] = value
    if (!changed && prev[name] !== value) changed = true
  }
  /*
   * ⚠️ **值没变就不要换对象。** 换掉 `snapshot.value` 会让所有依赖它的
   * computed 重算、组件重渲染 —— 而重渲染又会走到 `ensureThemeVar`。
   * 上面那条「键在就不再调度」已经能断开这个环，这里是第二道：
   * 就算有人绕过 `ensureThemeVars` 直接调 `readback()`，也不会自激。
   */
  if (changed) snapshot.value = next
}

/**
 * 主题换了 —— 把缓存丢掉并重读。
 *
 * ⚠️ 必须由**应用主题的人**调用（`applyTheme` 已经替你调了）。
 * 忘了调的后果是「换了主题，但连线还是旧颜色」—— 也就是这个模块
 * 本来要消灭的那个 bug。
 */
export function invalidateThemeRuntime(): void {
  snapshot.value = {}
  if (!hasDom()) return
  /*
   * 读回安排在下一帧，但**先清空**：清空会让 computed 立刻回落到兜底值，
   * 而不是继续显示上一個主题的颜色（宁可闪一下也不要留错的）。
   */
  scheduled = false
  scheduleReadback()
}

/** 同步读一个变量的当前值（不触发响应式） */
export function themeVar(name: string, fallback: string): string {
  const v = snapshot.value[name]
  return v ? v : fallback
}

/**
 * 响应式地读一个变量。
 *
 * @param name     变量名（含 `--`）
 * @param fallback 变量不存在时的兜底值。**一定要给** —— 见文件头纪律 2
 */
export function useThemeVar(name: string, fallback = ''): ComputedRef<string> {
  ensureThemeVar(name)
  return computed(() => {
    const v = snapshot.value[name]
    return v ? v : fallback
  })
}

/* ══════════════════════════════════════════════════════════════════
   语义化的便捷入口
   ══════════════════════════════════════════════════════════════════ */

/** 端口色变量名 */
export function socketVar(type: SocketType): string {
  return `--np-socket-${type}`
}

/** 分类色变量名 */
export function categoryVar(categoryId: string): string {
  return `--np-cat-${categoryId}`
}

/**
 * 同步读端口色。
 *
 * ⚠️ 兜底是**静态表**（`SOCKET_COLORS`）：它现在是「内置默认主题的值」，
 * 不再是唯一真相。这样主题没声明某个端口色时，行为退回改动前，不会变空。
 */
export function themeSocketColor(type: SocketType): string {
  const name = socketVar(type)
  ensureThemeVar(name)
  return themeVar(name, SOCKET_COLORS[type] ?? SOCKET_COLORS.any)
}

/**
 * 同步读分类色。
 *
 * ⚠️ 为什么读**两次**：主题只为**声明过**的分类写 `--np-cat-<id>`。
 * 装一个新节点包之后，它的分类没人声明过 —— 这时候退回
 * `--np-cat-default`（一个中性的分类色），而不是退回旧的「一片灰」。
 * 派生色由 `theme.css` 里 `--np-cat-default` 的定义承担，
 * 这样「未知分类」在 CSS 侧和 JS 侧表现一致。
 */
export function themeCategoryColor(categoryId: string | undefined, fallback: string): string {
  if (!categoryId) return fallback
  const name = categoryVar(categoryId)
  ensureThemeVar(name)
  ensureThemeVar('--np-cat-default')
  const own = themeVar(name, '')
  if (own) return own
  return themeVar('--np-cat-default', fallback)
}

/** 一批变量的响应式快照（给需要同时读好几个的地方） */
export function useThemeVars(
  spec: Record<string, string>,
): ComputedRef<Record<string, string>> {
  const names = Object.keys(spec)
  ensureThemeVars(names)
  return computed(() => {
    const out: Record<string, string> = {}
    for (const name of names) out[name] = snapshot.value[name] || spec[name]!
    return out
  })
}
